import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type LanguageOption = "en" | "ar";

const languageToggleBackgroundVariant = {
  en: { backgroundColor: "rgba(255,255,255,0.08)" },
  ar: { backgroundColor: "rgba(255,255,255,0.15)" },
};

const languageLabelVariants = {
  active: { color: "#000", opacity: 1 },
  inactive: { color: "rgba(255,255,255,0.7)", opacity: 0.7 },
};

export interface LanguageSwitcherProps {
  value?: LanguageOption;
  onChange?: (nextLanguage: LanguageOption) => void;
  initialValue?: LanguageOption;
}

export function LanguageSwitcher({ value, onChange, initialValue = "en" }: LanguageSwitcherProps) {
  const [internalLanguage, setInternalLanguage] = useState<LanguageOption>(value ?? initialValue);
  const language = value ?? internalLanguage;

  const labelContainerRef = useRef<HTMLDivElement>(null);
  const enLabelRef = useRef<HTMLSpanElement>(null);
  const arLabelRef = useRef<HTMLSpanElement>(null);

  const [labelMetrics, setLabelMetrics] = useState<{
    containerWidth: number;
    en: { width: number; left: number };
    ar: { width: number; left: number };
  }>({
    containerWidth: 0,
    en: { width: 0, left: 0 },
    ar: { width: 0, left: 0 },
  });

  useEffect(() => {
    if (value) {
      setInternalLanguage(value);
    }
  }, [value]);

  const handleToggleLanguage = useCallback(() => {
    const nextLanguage: LanguageOption = language === "en" ? "ar" : "en";
    if (value === undefined) {
      setInternalLanguage(nextLanguage);
    }
    onChange?.(nextLanguage);
  }, [language, onChange, value]);

  useLayoutEffect(() => {
    const container = labelContainerRef.current;
    const enLabel = enLabelRef.current;
    const arLabel = arLabelRef.current;
    if (!container || !enLabel || !arLabel) {
      return;
    }

    const updateMetrics = () => {
      const containerRect = container.getBoundingClientRect();
      const enRect = enLabel.getBoundingClientRect();
      const arRect = arLabel.getBoundingClientRect();

      setLabelMetrics({
        containerWidth: containerRect.width,
        en: {
          width: enRect.width,
          left: enRect.left - containerRect.left,
        },
        ar: {
          width: arRect.width,
          left: arRect.left - containerRect.left,
        },
      });
    };

    updateMetrics();

    const supportsResizeObserver = typeof window !== "undefined" && "ResizeObserver" in window;
    let observer: ResizeObserver | null = null;

    if (supportsResizeObserver) {
      observer = new ResizeObserver(updateMetrics);
      observer.observe(container);
      observer.observe(enLabel);
      observer.observe(arLabel);
    } else {
      window.addEventListener("resize", updateMetrics);
    }

    return () => {
      observer?.disconnect();
      if (!supportsResizeObserver) {
        window.removeEventListener("resize", updateMetrics);
      }
    };
  }, []);

  const bubblePadding = 8;
  const currentMetrics = labelMetrics[language];
  const bubbleWidth = Math.max(currentMetrics.width + bubblePadding * 2, 32);
  const maxLeft = Math.max(labelMetrics.containerWidth - bubbleWidth, 0);
  const targetLeft = currentMetrics.left - bubblePadding;
  const bubbleLeft = Math.max(0, Math.min(targetLeft, maxLeft));

  return (
    <Box
      component={motion.button}
      type="button"
      onClick={handleToggleLanguage}
      role="switch"
      aria-checked={language === "ar"}
      aria-label={`Switch to ${language === "en" ? "Arabic" : "English"}`}
      initial={language}
      animate={language}
      variants={languageToggleBackgroundVariant}
      transition={{ duration: 0.25 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      sx={{
        borderRadius: "999px",
        border: "1px solid rgba(255,255,255,0.35)",
        width: 116,
        height: 38,
        padding: "4px",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        textTransform: "uppercase",
        overflow: "hidden",
        backgroundColor: "rgba(255,255,255,0.08)",
      }}
    >
      <Box
        component={motion.span}
        initial={false}
        animate={{
          left: bubbleLeft,
          width: bubbleWidth,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 24 }}
        sx={{
          position: "absolute",
          left: 0,
          top: 4,
          height: "calc(100% - 8px)",
          borderRadius: "999px",
          backgroundColor: "#fff",
          boxShadow: "0 12px 20px rgba(0,0,0,0.25)",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.25,
          zIndex: 2,
          fontSize: 11,
          letterSpacing: "0.2em",
          fontWeight: 600,
          textTransform: "uppercase",
        }}
        ref={labelContainerRef}
      >
        <Typography
          component={motion.span}
          variant="caption"
          initial="inactive"
          animate={language === "en" ? "active" : "inactive"}
          variants={languageLabelVariants}
          transition={{ duration: 0.18 }}
          sx={{ display: "inline-flex" }}
          ref={enLabelRef}
        >
          EN
        </Typography>
        <Typography
          component={motion.span}
          variant="caption"
          initial="inactive"
          animate={language === "ar" ? "active" : "inactive"}
          variants={languageLabelVariants}
          transition={{ duration: 0.18 }}
          sx={{ display: "inline-flex" }}
          ref={arLabelRef}
        >
          AR
        </Typography>
      </Box>
    </Box>
  );
}
